import { computed, effect, Inject, Injectable, signal, WritableSignal } from '@angular/core';
import { NgbOffcanvas, NgbOffcanvasRef } from "@ng-bootstrap/ng-bootstrap";
import { FilterComponent } from '../category/filter/filter.component';
import { LocalstorageService } from "@gea-waldviertler-website/shop-lib";
import {
  Category,
  CategoryPageContentInterface,
  convertMapToObject,
  FilterAttributes,
  FilterSelectionInterface,
  FilterSelectionShortInterface,
  FilterSelectionsInterface,
  mapsAreEqual,
  Product,
  ProductAttributeInterface,
  ProductAttributeOptionInterface,
  ProductVariantInterface,
  SortOptionInterface
} from "@gea-waldviertler-website/gea-models";
import { PageService } from "@gea-waldviertler-website/shop-lib";

@Injectable({
  providedIn: 'root'
})
export class FilterService {

  // categories
  mainCategory? : Category
  titleCategory? : Category

  // products
  genericProductAttributes: FilterAttributes = {
    instock: {
      code: "instock",
      title: "Verfügbarkeit",
      label: "Verfügbarkeit",
      scope: "p",
      filterFn: (f: FilterSelectionShortInterface, p: Product<ProductVariantInterface>) => {
        return !! p.inStock || !! p.inStoreStock
      },
      options: [
        {
          optionIds: [1],
          title: "Nur verfügbare Produkte anzeigen"
        }
      ]
    }
  }
  // attributes
  productAttributes = signal<Map<string, ProductAttributeInterface> | undefined>(undefined)
  private sortSignal = signal<SortOptionInterface>({ code: "rel",   label: "Relevanz" })
  scope?: string

  filterAttributes = signal<FilterAttributes | undefined>(undefined)
  private filterSelections: WritableSignal<FilterSelectionsInterface | undefined> = signal(undefined)

  private offcanvasRef?: NgbOffcanvasRef

  count = computed<number>(() => {
    const fs = this.filterSelections()
    if (! fs) {
      return 0
    }

    let count = 0

    for (const f of Object.values(fs)) {
      count += f.selections ? f.selections.length : 0
    }

    return count
  })
  
  storePickupEnabled: boolean = false
  
  constructor(
    @Inject(NgbOffcanvas) public offcanvas: NgbOffcanvas,
    @Inject(LocalstorageService) private storage: LocalstorageService,
    @Inject(PageService) private pageService: PageService,
  ) {
    this.pageService.getConfigSubject().subscribe(config => {
      if (config) this.storePickupEnabled = config.storePickupEnabled
    })

    // add shop event filter by shop event signal
    effect(() => {
      const se = this.pageService.getActiveShopEventsSignal()()
      this.filterAttributes.update((fa) => {
        return this.handleShopEventsFilter(fa, !!(se && se.size > 0))
      })
      this.filterSelections.update((fs) => {
        if (! fs) return undefined
        if (! se || ! se.size) {
          if (fs.hasOwnProperty("hasevent")) {
            delete fs["hasevent"]
          }
          let lsfs = this.storage.get(this.getFilterStorageKey())
          if (lsfs.hasOwnProperty("hasevent")) {
            delete lsfs['hasevent']
            this.storage.set(this.getFilterStorageKey(), lsfs)
          }
        }
        return { ... fs }
      })
    })

    // add generic filters by productAttributes signal
    effect(() => {
      const productAttributes = this.productAttributes()
      this.filterAttributes.update((fa) => {
        if (fa) return {
          ... this.genericProductAttributes, ... fa, ... convertMapToObject(productAttributes) as FilterAttributes
        }
        else return {
            ... this.genericProductAttributes, ... convertMapToObject(productAttributes) as FilterAttributes
          }
      })
    })
  }

  getFilterSelections() {
    return this.filterSelections.asReadonly()
  }

  private handleShopEventsFilter(fa: FilterAttributes | undefined, add: boolean) {
    if (this.mainCategory?.isSale) {
      if (fa && fa["hasevent"]) {
        return fa
      } else {
        return { ...fa, ...this.getShopEventsFilter() }
      }
    }
    if (add) {
      if (fa) {
        return {
          ...fa, ...this.getShopEventsFilter()
        }
      } else {
        return this.getShopEventsFilter()
      }
    } else {
      if (fa) {
        delete fa["hasevent"]
        return { ... fa }
      } else {
        return {}
      }
    }
  }

  private getShopEventsFilter(): FilterAttributes {
    let options: ProductAttributeOptionInterface[] = []

    const events = this.pageService.events()
    if (events) {
      for (const [id , event] of events) {
        options.push({ optionIds: [ id ], title: event.name })
      }
    }

    return {
      hasevent: {
        code: "hasevent",
        title: "Aktion",
        label: "Aktion",
        scope: "p",
        filterFn: (f: FilterSelectionShortInterface, p: Product<ProductVariantInterface>) => {
          if (p.activeShopEvents && f.selections) {
            // Why TF does this not work?
            // if (p.activeShopEvents.hasAny(f.selections)) return true
            for (const s of f.selections) {
              if (p.activeShopEvents.has(s)) return true
            }
          }
          return false
        },
        options: options
      }
    }
  }

  apply(fs: FilterSelectionsInterface) {
    this.filterSelections.set(fs)
    this.storage.set(this.getFilterStorageKey(), fs)
  }

  reset() {
    this.filterSelections.set(undefined)
    this.storage.remove(this.getFilterStorageKey())
  }

  // offcanvas
  show() {
    this.offcanvasRef = this.offcanvas.open(FilterComponent, { scroll: true, position: 'end'})
    if (this.offcanvasRef) {
      this.offcanvasRef.dismissed.subscribe(dismissed => {
        this.filterSelections.set(this.storage.get(this.getFilterStorageKey()))
      })
    }
  }

  hide() {
    this.offcanvas.dismiss()
  }

  getFilteredSwatch(): FilterSelectionInterface | undefined {
    var swatchAttribute = undefined
    const pa = this.productAttributes()
    if(! pa) {
      return undefined
    }

    for(var attribute of pa.values()) {
      let isSwatch: boolean = false
      for(var option of attribute.options) {
        if(option.imageSlug) {
          isSwatch = true
          break
        }
      }

      if(isSwatch) {
        swatchAttribute = attribute.code
        break
      }
    }

    const fs = this.filterSelections()
    if (fs) {
      if (swatchAttribute == "farbe") {
        return fs["color_group"]
      } else if (swatchAttribute) {
        return fs[swatchAttribute]
      }
    }
    return undefined
  }

  private getFilterStorageKey() {
    return 'filter_' + this.scope;
  }

  public loadCategoryPage(cpc: CategoryPageContentInterface) {
    this.mainCategory = cpc.mainCategory
    this.titleCategory = cpc.currentCategory
    if (! mapsAreEqual(this.productAttributes(), cpc.productAttributes)) {
      this.productAttributes.set(cpc.productAttributes)
    }
    this.scope = 'cat_' + cpc.currentCategory.id
    this.filterSelections.set(cpc.filterSelections)
  }

  filterAndSortListForView(l: Product<ProductVariantInterface>[]) {
    let filterAttributes = this.filterAttributes()
    let filters = this.filterSelections()

    let filtered: Product<ProductVariantInterface>[] = []
    for (const product of l) {
      let show = true
      if (filters) {
        Object.entries(filters).forEach(([ prop, filter ]) => {
          if (filterAttributes && filterAttributes.hasOwnProperty(prop) && filterAttributes[prop].filterFn) {
            show = show && filterAttributes[prop].filterFn(filter, product)
          } else {
            show = show && this.checkFilter(filter, product)
          }
        })
      }
      if (show) filtered.push(product)
    }
    this.applySort(filtered)
    return filtered
  }

  /**
   * returns true if the product matches the filter or if the filter is set to null
   *
   * @param filter
   * @param product
   */
  checkFilter(filter: FilterSelectionInterface, product: Product<ProductVariantInterface>) {
    if (filter.selections === null || filter.selections.length === 0) {
      return true
    }

    let show = false

    if (filter.housing == "variant.attributes") {
      for (const variant of product.variants) {
        if (! product.canBeOrdered(this.storePickupEnabled, 1, variant.variantId)) {
          show = false
          break
        }
        if (variant.attributes && variant.attributes.has(filter.attributeCode)) {
          const attributeId = variant.attributes.get(filter.attributeCode)
          if (attributeId && filter.selections?.includes(attributeId)) {
            show = true
            break
          }
        }
      }
    } else if (filter.housing == "variant.childAttributes") {
      for (const variant of product.variants) {
        if (! product.canBeOrdered(this.storePickupEnabled, 1, variant.variantId)) {
          show = false
          break
        }
        if (variant.childAttributes && variant.childAttributes.has(filter.attributeCode)) {
          for (const optionId of filter.selections!) {
            const attOptId = variant.childAttributes.get(filter.attributeCode)
            if (attOptId && attOptId.includes(optionId)) {
              show = !! product.canBeOrdered(this.storePickupEnabled, 1, variant.variantId, optionId)
              break
            }
          }
          if (show) {
            break
          }
        }
      }
    }

    return show
  }

  setSort(sort: SortOptionInterface): void {
    this.sortSignal.set({ ...sort })
    this.storage.set('sorter_' + this.scope, sort)
  }

  getSortSignal() {
    return this.sortSignal.asReadonly()
  }
  /**
   * Set sorting of the collection. This will not affect the collection or the items property itself, but will rebuild the sorting array
   *
   * @param sort
   */
  applySort(l: Product<ProductVariantInterface>[]): void {
    let sort = this.sortSignal()
    if (! sort) sort = { code: 'rel', label: "Relevanz" }
    switch (sort.code) {
      // sort by bestseller
      case "best":
        l.sort((a, b) => {
          return b.getVariant().sellingScore - a.getVariant().sellingScore
        });
        break
      // sort by title alphanumeric
      case "alpha":
        l.sort((a, b) => {
          if (a.title < b.title) {
            return -1
          }
          if (a.title > b.title) {
            return 1
          }
          return 0
        });
        break
      // sort by price ascending
      case "pasc":
        l.sort((a, b) => {
          const price_a = a.specialPriceFrom ?? a.priceFrom
          const price_b = b.specialPriceFrom ?? b.priceFrom
          return price_a - price_b
        });
        break
      // sort by price descending
      case "pdesc":
        l.sort((a, b) => {
          const price_a = a.specialPriceFrom ?? a.priceFrom
          const price_b = b.specialPriceFrom ?? b.priceFrom
          return price_b - price_a
        });
        break
      // sort by relevance by default (sort.code = "rel")
      default:
        l.sort((a, b) => {
          return b.getVariant().visibilityScore - a.getVariant().visibilityScore
        })
    }
  }

  public static getFilterType(filterSelection: FilterSelectionShortInterface): "single" | "multi" | "range" {
    return "multi"
  }

  public static getFilterHousing(filterSelection: FilterSelectionShortInterface): "variant.attributes" | "variant.childAttributes" | "product.attributes" {
    let map = {
      leiste: "variant.childAttributes",
      schuhgroeße: "variant.childAttributes",
      schuhgroesse: "variant.childAttributes",
      color_group: "variant.attributes",
      model: "variant.attributes",
    }
    if (filterSelection.attributeCode) {
      if (map.hasOwnProperty(filterSelection.attributeCode)) {
        // @ts-ignore
        return map[filterSelection.attributeCode]
      }
    }
    return "variant.attributes"
  }
}
